// ChartViewerPage — Full-screen TradingView-style chart with trade replay
// Layout: top nav w/ pair selector, full-height SVG candlestick chart, indicator panel,
// drawing tools, replay controls (play/pause/forward/back/loop), trade markers, depth bars

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfTimeRange } from '../../components/TfPrimitives';

interface Candle { o: number; h: number; l: number; c: number; v: number; t: number }
interface Trade { id: string; side: 'buy' | 'sell'; entry: number; exit?: number; qty: number; pnl?: number; t: number; note?: string }
interface Drawing { id: string; type: 'hline' | 'trendline' | 'rect' | 'text'; points: number[]; color: string; text?: string }

const PAIRS = [
  { key: 'ES',  name: 'E-mini S&P',   emoji: '📈', tick: 0.25, val: 12.5, color: '#007AFF' },
  { key: 'NQ',  name: 'E-mini NQ',    emoji: '⚡', tick: 0.25, val: 5,    color: '#5856D6' },
  { key: 'CL',  name: 'Crude Oil',    emoji: '🛢️', tick: 0.01, val: 10,   color: '#1C1C1E' },
  { key: 'GC',  name: 'Gold',         emoji: '🥇', tick: 0.1,  val: 10,   color: '#FF9500' },
  { key: 'BTC', name: 'Bitcoin',      emoji: '₿',  tick: 5,    val: 5,    color: '#FF3B30' },
  { key: 'YM',  name: 'E-mini Dow',   emoji: '📉', tick: 1,    val: 5,    color: '#34C759' },
];

const TIMEFRAMES = ['1m', '5m', '15m', '1H', '4H', '1D', '1W'] as const;
const INDICATORS = ['MA 20', 'MA 50', 'EMA 9', 'RSI', 'MACD', 'BB', 'VWAP', 'Volume'];
const RANGE_OPTIONS = ['1D', '1W', '1M', '3M', '6M', '1Y'] as const;

export function ChartViewerPage() {
  const nav = useNavigate();
  const [pair, setPair] = useState('ES');
  const [tf, setTf] = useState<typeof TIMEFRAMES[number]>('15m');
  const [range, setRange] = useState<typeof RANGE_OPTIONS[number]>('1D');
  const [tool, setTool] = useState<'cursor' | 'hline' | 'trendline' | 'rect' | 'text' | 'measure'>('cursor');
  const [indicators, setIndicators] = useState<string[]>(['MA 20', 'Volume']);
  const [playing, setPlaying] = useState(false);
  const [replayIdx, setReplayIdx] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [showPairPicker, setShowPairPicker] = useState(false);
  const [showIndPanel, setShowIndPanel] = useState(false);
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const playRef = useRef<number | null>(null);

  const p = PAIRS.find((x) => x.key === pair)!;

  // Generate synthetic candles
  const candles: Candle[] = (() => {
    const n = 80;
    const out: Candle[] = [];
    let o = pair === 'ES' ? 5280 : pair === 'NQ' ? 18250 : pair === 'CL' ? 78 : pair === 'GC' ? 2350 : pair === 'BTC' ? 62000 : 39000;
    for (let i = 0; i < n; i++) {
      const h = o + Math.abs(Math.sin(i * 0.3) * 8) + Math.random() * 4;
      const l = o - Math.abs(Math.cos(i * 0.3) * 6) - Math.random() * 4;
      const c = l + Math.random() * (h - l);
      out.push({ o, h, l, c, v: 1000 + Math.random() * 5000, t: i });
      o = c;
    }
    return out;
  })();

  // Synthetic trades on this chart
  const trades: Trade[] = [
    { id: 't1', side: 'buy',  entry: candles[20].c, exit: candles[32].c, qty: 2, pnl: 420, t: 20, note: 'Breakout entry' },
    { id: 't2', side: 'sell', entry: candles[45].c, exit: candles[52].c, qty: 1, pnl: -85, t: 45, note: 'Failed breakout' },
    { id: 't3', side: 'buy',  entry: candles[60].c, exit: candles[72].c, qty: 3, pnl: 1240, t: 60, note: 'Trend continuation' },
  ];

  // Compute MAs
  const ma20 = candles.map((_, i) => {
    if (i < 19) return null;
    let s = 0;
    for (let j = i - 19; j <= i; j++) s += candles[j].c;
    return s / 20;
  });
  const ma50 = candles.map((_, i) => {
    if (i < 49) return null;
    let s = 0;
    for (let j = i - 49; j <= i; j++) s += candles[j].c;
    return s / 50;
  });

  // Auto-play replay
  useEffect(() => {
    if (playing) {
      playRef.current = window.setInterval(() => {
        setReplayIdx((i) => {
          if (i >= candles.length - 1) { setPlaying(false); return i; }
          return i + 1;
        });
      }, 500 / speed);
      return () => { if (playRef.current) clearInterval(playRef.current); };
    }
  }, [playing, speed, candles.length]);

  // Chart geometry
  const W = 414, H = 320;
  const PAD = 8;
  const visibleCandles = candles.slice(0, Math.max(20, replayIdx + 1));
  const min = Math.min(...visibleCandles.map((c) => c.l));
  const max = Math.max(...visibleCandles.map((c) => c.h));
  const priceRange = max - min || 1;
  const cw = (W - PAD * 2) / visibleCandles.length;
  const ch = H * 0.75 - PAD;
  const cy = (price: number) => PAD + (1 - (price - min) / priceRange) * ch;

  // Hero price/change
  const currentPrice = candles[replayIdx]?.c || candles[candles.length - 1].c;
  const startPrice = candles[0].o;
  const changeAbs = currentPrice - startPrice;
  const changePct = (changeAbs / startPrice) * 100;
  const positive = changePct >= 0;

  function toggleInd(name: string) {
    setIndicators((curr) => curr.includes(name) ? curr.filter((x) => x !== name) : [...curr, name]);
  }

  return (
    <div className="min-h-screen flex flex-col text-white" style={{ background: '#0A0A0A' }}>
      {/* Top nav */}
      <div className="flex h-11 items-center justify-between border-b px-2 backdrop-blur-xl" style={{ backgroundColor: 'rgba(10,10,10,0.85)', borderColor: 'var(--tf-border)' }}>
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button onClick={() => setShowPairPicker(true)} data-testid="pair-picker" className="flex items-center gap-1.5 text-[15px] font-semibold">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full text-white text-[14px]"
            style={{ background: p.color }}
          >
            {p.emoji}
          </span>
          <span>{p.key}</span>
          <span className="text-[11px] text-tf-text-3">{p.name}</span>
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={() => nav('/portal/trade/t1')} data-testid="trade-list" className="flex h-9 w-9 items-center justify-center text-tf-blue">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </button>
          <button onClick={() => setShowIndPanel(true)} data-testid="indicators-btn" className="flex h-9 w-9 items-center justify-center text-tf-blue">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 17l4-4 4 4 6-6 4 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>

      {/* Asset header */}
      <div className="px-4 pt-2">
        <div className="flex items-baseline gap-2">
          <span className="text-[34px] font-bold tabular-nums tracking-tight" style={{ color: '#1C1C1E' }}>
            ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[13px]">
          <span className="font-semibold" style={{ color: positive ? '#34C759' : '#FF3B30' }}>
            {positive ? '+' : ''}{changePct.toFixed(2)}%
          </span>
          <span style={{ color: '#8E8E93' }}>
            {positive ? '+' : ''}${Math.abs(changeAbs).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="flex items-center gap-0.5" style={{ color: '#8E8E93' }}>
            Today
            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>
      </div>

      {/* Timeframe tabs */}
      <div className="px-2 py-1.5 border-b flex items-center gap-1 overflow-x-auto" style={{ borderColor: 'var(--tf-border)' }}>
        {TIMEFRAMES.map((t) => (
          <button
            key={t}
            onClick={() => setTf(t)}
            data-testid={`tf-${t}`}
            className={['shrink-0 rounded-[8px] px-2.5 py-1 text-[12px] font-semibold transition',
              tf === t ? 'bg-white text-black' : 'text-tf-text-3 active:bg-tf-text-1'
            ].join(' ')}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto text-[10px] text-tf-text-3 tabular-nums">O {candles[replayIdx]?.o.toFixed(2)} H {candles[replayIdx]?.h.toFixed(2)} L {candles[replayIdx]?.l.toFixed(2)} C {candles[replayIdx]?.c.toFixed(2)}</div>
      </div>

      {/* Chart */}
      <div className="flex-1 relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
          {/* Grid */}
          {Array.from({ length: 6 }).map((_, i) => (
            <line key={i} x1={PAD} y1={PAD + (i * ch / 5)} x2={W - PAD} y2={PAD + (i * ch / 5)} stroke="#1C1C1E" strokeWidth="0.5" />
          ))}
          {Array.from({ length: 6 }).map((_, i) => {
            const price = max - (i * priceRange / 5);
            return <text key={i} x={W - PAD - 2} y={PAD + (i * ch / 5) + 3} textAnchor="end" fontSize="9" fill="#8E8E93">{price.toFixed(2)}</text>;
          })}

          {/* Candles */}
          {visibleCandles.map((c, i) => {
            const x = PAD + i * cw + cw / 2;
            const yo = cy(c.o), yc = cy(c.c), yh = cy(c.h), yl = cy(c.l);
            const bull = c.c >= c.o;
            return (
              <g key={i}>
                <line x1={x} y1={yh} x2={x} y2={yl} stroke={bull ? '#34C759' : '#FF3B30'} strokeWidth="1" />
                <rect x={x - cw * 0.35} y={Math.min(yo, yc)} width={cw * 0.7} height={Math.max(1, Math.abs(yc - yo))} fill={bull ? '#34C759' : '#FF3B30'} />
              </g>
            );
          })}

          {/* MAs */}
          {indicators.includes('MA 20') && (
            <polyline
              fill="none" stroke="#007AFF" strokeWidth="1.5"
              points={ma20.map((v, i) => v ? `${PAD + i * cw + cw / 2},${cy(v)}` : '').filter(Boolean).join(' ')}
            />
          )}
          {indicators.includes('MA 50') && (
            <polyline
              fill="none" stroke="#FF9500" strokeWidth="1.5"
              points={ma50.map((v, i) => v ? `${PAD + i * cw + cw / 2},${cy(v)}` : '').filter(Boolean).join(' ')}
            />
          )}

          {/* Trade markers */}
          {trades.map((t) => {
            const x = PAD + t.t * cw + cw / 2;
            const y = cy(t.entry);
            return (
              <g key={t.id} data-testid={`marker-${t.id}`}>
                <circle cx={x} cy={y} r="4" fill={t.side === 'buy' ? '#34C759' : '#FF3B30'} stroke="white" strokeWidth="1" />
                <text x={x} y={y - 8} textAnchor="middle" fontSize="8" fill={t.side === 'buy' ? '#34C759' : '#FF3B30'} fontWeight="bold">{t.side === 'buy' ? '▲' : '▼'}</text>
              </g>
            );
          })}

          {/* Current price line */}
          <line x1={PAD} y1={cy(candles[replayIdx]?.c || candles[candles.length - 1].c)} x2={W - PAD} y2={cy(candles[replayIdx]?.c || candles[candles.length - 1].c)} stroke="#FF9500" strokeWidth="0.5" strokeDasharray="2 2" />

          {/* Drawing tools overlay */}
          {drawings.map((d) => {
            if (d.type === 'hline') {
              return <line key={d.id} x1={PAD} y1={cy(d.points[0])} x2={W - PAD} y2={cy(d.points[0])} stroke={d.color} strokeWidth="1" strokeDasharray="3 3" />;
            }
            return null;
          })}

          {/* Replay cursor (vertical line) */}
          {replayIdx > 0 && (
            <line x1={PAD + replayIdx * cw + cw / 2} y1={PAD} x2={PAD + replayIdx * cw + cw / 2} y2={H * 0.75} stroke="#007AFF" strokeWidth="1" strokeDasharray="2 2" />
          )}

          {/* Volume bars (bottom 25%) */}
          {visibleCandles.map((c, i) => {
            const x = PAD + i * cw + cw / 2;
            const maxV = Math.max(...visibleCandles.map((v) => v.v));
            const bh = (c.v / maxV) * (H * 0.2);
            const y = H - PAD - bh;
            return <rect key={i} x={x - cw * 0.35} y={y} width={cw * 0.7} height={bh} fill={c.c >= c.o ? '#34C759' : '#FF3B30'} opacity="0.4" />;
          })}
        </svg>

        {/* Drawing tools floating panel */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {([
            { key: 'cursor',     icon: 'M3 3l7.07 16.97 2.51-7.39 7.39-2.51z', label: 'Cursor' },
            { key: 'hline',      icon: 'M3 12h18', label: 'Horizontal line' },
            { key: 'trendline',  icon: 'M3 17l6-6 4 4 8-8', label: 'Trendline' },
            { key: 'rect',       icon: 'M3 5h18v14H3z', label: 'Rectangle' },
            { key: 'text',       icon: 'M4 7V4h16v3M9 20h6M12 4v16', label: 'Text' },
            { key: 'measure',    icon: 'M21 3L3 21M3 3l18 18', label: 'Measure' },
          ] as const).map((t) => (
            <button
              key={t.key}
              onClick={() => { setTool(t.key); if (t.key === 'hline') setDrawings((d) => [...d, { id: String(Date.now()), type: 'hline', points: [candles[replayIdx].c + 5], color: '#FF9500' }]); }}
              data-testid={`tool-${t.key}`}
              className={['flex h-9 w-9 items-center justify-center rounded-[8px] transition',
                tool === t.key ? 'bg-tf-blue text-white' : 'bg-tf-text-1/80 text-white active:bg-tf-text-1'
              ].join(' ')}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d={t.icon} strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          ))}
        </div>

        {/* Replay controls */}
        <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 rounded-[12px] p-2" style={{ background: 'rgba(28, 28, 30, 0.95)', backdropFilter: 'blur(12px)' }}>
          <button
            onClick={() => { setPlaying(!playing); }}
            data-testid="replay-play"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-tf-blue text-white"
          >
            {playing ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
          <button onClick={() => setReplayIdx(Math.max(0, replayIdx - 5))} data-testid="replay-back" className="flex h-9 w-9 items-center justify-center rounded-full text-tf-text-3 active:bg-white/10">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 19l-7-7 7-7M19 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button onClick={() => setReplayIdx(Math.min(candles.length - 1, replayIdx + 5))} data-testid="replay-fwd" className="flex h-9 w-9 items-center justify-center rounded-full text-tf-text-3 active:bg-white/10">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 5l7 7-7 7M5 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <div className="flex-1">
            <input
              type="range"
              min={0}
              max={candles.length - 1}
              value={replayIdx}
              onChange={(e) => { setPlaying(false); setReplayIdx(Number(e.target.value) || 0); }}
              data-testid="replay-slider"
              className="w-full accent-tf-blue"
            />
          </div>
          <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} data-testid="replay-speed" className="bg-tf-text-1 text-white text-[11px] rounded-full px-2 py-1 focus:outline-none">
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={4}>4x</option>
          </select>
        </div>
      </div>

      {/* Time range pills (1D 7D 1M 3M 6M 1Y) */}
      <div className="border-t" style={{ borderColor: 'var(--tf-border)', background: 'rgba(10,10,10,0.6)' }}>
        <TfTimeRange value={range} onChange={setRange} options={[...RANGE_OPTIONS]} />
      </div>

      {/* Trade list strip */}
      <div className="border-t p-2" style={{ borderColor: 'var(--tf-border)', background: '#0A0A0A' }}>
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-tf-text-3">Trades on chart</span>
          <span className="text-[10px] text-tf-text-3">{trades.length} trades · +${trades.reduce((s, t) => s + (t.pnl || 0), 0)}</span>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {trades.map((t) => (
            <button
              key={t.id}
              onClick={() => setReplayIdx(t.t)}
              data-testid={`chart-trade-${t.id}`}
              className="shrink-0 rounded-[10px] border px-3 py-1.5 active:scale-95 transition"
              style={{ borderColor: 'var(--tf-border)', background: '#1C1C1E' }}
            >
              <div className="flex items-center gap-1.5">
                <span className={['text-[10px] font-bold', t.side === 'buy' ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>{t.side === 'buy' ? 'BUY' : 'SELL'}</span>
                <span className="text-[12px] font-semibold text-white">{t.entry.toFixed(2)}</span>
              </div>
              <div className={['text-[11px] font-bold tabular-nums', (t.pnl || 0) >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {t.pnl !== undefined && (t.pnl >= 0 ? '+' : '')}${Math.abs(t.pnl || 0)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Pair picker sheet */}
      {showPairPicker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowPairPicker(false)}>
          <div className="w-full max-w-[420px] rounded-t-[20px] p-4 pb-6 text-tf-text-1" style={{ background: 'var(--tf-bg)' }} onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-tf-border" />
            <h2 className="px-1 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Choose pair</h2>
            <div className="space-y-1">
              {PAIRS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => { setPair(p.key); setShowPairPicker(false); }}
                  data-testid={`pick-${p.key}`}
                  className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 active:bg-tf-surface-1"
                >
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white text-[18px]"
                    style={{ background: p.color }}
                  >
                    {p.emoji}
                  </span>
                  <div className="flex-1 text-left">
                    <div className="text-[14px] font-semibold text-tf-text-1">{p.key}</div>
                    <div className="text-[11px] text-tf-text-3">{p.name} · tick ${p.val}</div>
                  </div>
                  {pair === p.key && <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Indicators panel */}
      {showIndPanel && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowIndPanel(false)}>
          <div className="w-full max-w-[420px] rounded-t-[20px] p-4 pb-6 text-tf-text-1" style={{ background: 'var(--tf-bg)' }} onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-tf-border" />
            <h2 className="px-1 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Indicators</h2>
            <p className="px-1 pb-3 text-[13px] text-tf-text-3">Tap to add/remove</p>
            <div className="grid grid-cols-2 gap-2">
              {INDICATORS.map((name) => (
                <button
                  key={name}
                  onClick={() => toggleInd(name)}
                  data-testid={`ind-${name}`}
                  className={['flex h-12 items-center justify-between rounded-[12px] border px-3 transition',
                    indicators.includes(name) ? 'border-tf-blue bg-tf-blue/[0.05]' : 'border-tf-border bg-tf-bg active:opacity-40'
                  ].join(' ')}
                >
                  <span className="text-[13px] font-semibold text-tf-text-1">{name}</span>
                  {indicators.includes(name) && <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
